/**
 * Restaurant Settings Service
 * 
 * Stores and retrieves restaurant-specific settings using localStorage.
 * These settings are per-device but can be synced to Supabase in the future.
 */

const SETTINGS_KEY = 'restaurant_settings';

export interface RestaurantSettings {
  // Maximum tables that can be occupied at any given time slot
  // Set to 0 or null to disable this limit (use all available tables)
  maxTablesOccupied: number | null;
  // Maximum chairs allowed per table (global default/fallback: 10)
  maxTableSeats: number;
  
  // Future settings can be added here:
  // defaultReservationDuration: number; // in minutes
  // allowWalkIns: boolean;
  // etc.
}

const DEFAULT_SETTINGS: RestaurantSettings = {
  maxTablesOccupied: null, // null means no limit (use all tables)
  maxTableSeats: 10,
};

/**
 * Get current restaurant settings
 */
export function getSettings(): RestaurantSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<RestaurantSettings>;
      const maxTableSeats =
        typeof parsed.maxTableSeats === 'number' && parsed.maxTableSeats >= 1
          ? Math.floor(parsed.maxTableSeats)
          : DEFAULT_SETTINGS.maxTableSeats;
      return {
        ...DEFAULT_SETTINGS,
        ...parsed,
        maxTableSeats,
      };
    }
  } catch (error) {
    console.error('[restaurantSettings] Error reading settings:', error);
  }
  return DEFAULT_SETTINGS;
}

/**
 * Save restaurant settings
 */
export function saveSettings(settings: Partial<RestaurantSettings>): RestaurantSettings {
  try {
    const current = getSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
    return updated;
  } catch (error) {
    console.error('[restaurantSettings] Error saving settings:', error);
    throw error;
  }
}

/**
 * Get max tables that can be occupied at once
 * Returns null if no limit is set
 */
export function getMaxTablesOccupied(): number | null {
  return getSettings().maxTablesOccupied;
}

/**
 * Set max tables that can be occupied at once
 * Pass null or 0 to disable the limit
 */
export function setMaxTablesOccupied(value: number | null): void {
  saveSettings({ maxTablesOccupied: value && value > 0 ? value : null });
}

/**
 * Get max chairs allowed per table (global)
 */
export function getMaxTableSeats(): number {
  const value = getSettings().maxTableSeats;
  return Number.isFinite(value) && value >= 1 ? value : DEFAULT_SETTINGS.maxTableSeats;
}

/**
 * Set max chairs allowed per table (global)
 */
export function setMaxTableSeats(value: number): void {
  const normalized = Math.max(1, Math.floor(value));
  saveSettings({ maxTableSeats: normalized });
}

export default {
  getSettings,
  saveSettings,
  getMaxTablesOccupied,
  setMaxTablesOccupied,
  getMaxTableSeats,
  setMaxTableSeats,
};

